import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { STORAGE_KEYS } from './storageService';

const SwitcherContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LanguageIcon = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: white;
  color: #2c3e50;
  border: 1px solid #ccc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f8f8f8;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  min-width: 120px;
  z-index: 1000;
  display: ${props => (props.$isOpen ? 'block' : 'none')};
  animation: ${fadeIn} 0.3s ease-out;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 14px;
    width: 12px;
    height: 12px;
    background-color: white;
    transform: rotate(45deg);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-left: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

const LanguageOption = styled.div<{ $isActive: boolean }>`
  padding: 8px 16px;
  cursor: pointer;
  color: ${props => (props.$isActive ? '#4ECDC4' : '#333')};
  background-color: ${props => (props.$isActive ? 'rgba(78, 205, 196, 0.1)' : 'transparent')};
  font-weight: ${props => (props.$isActive ? '500' : 'normal')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background-color: ${props => (props.$isActive ? 'rgba(78, 205, 196, 0.15)' : '#f5f5f5')};
  }
`;

const LanguageName = styled.span`
  font-size: 14px;
`;

const CheckIcon = styled.span`
  color: #4ECDC4;
  font-size: 14px;
  margin-left: 8px;
`;

const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    
    // 保存语言设置到 localStorage
    try {
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, JSON.stringify(language));
    } catch (error) {
      console.error('保存语言设置失败:', error);
    }
    
    // 语言选择后关闭下拉菜单
    setIsOpen(false);
  };
  
  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <SwitcherContainer ref={dropdownRef}>
      <LanguageIcon onClick={toggleDropdown}>
        🌐
      </LanguageIcon>
      
      <DropdownMenu $isOpen={isOpen}>
        <LanguageOption 
          $isActive={i18n.language === 'en'} 
          onClick={() => changeLanguage('en')}
        >
          <LanguageName>{t('language.en')}</LanguageName>
          {i18n.language === 'en' && <CheckIcon>✓</CheckIcon>}
        </LanguageOption>
        <LanguageOption 
          $isActive={i18n.language === 'zh'} 
          onClick={() => changeLanguage('zh')}
        >
          <LanguageName>{t('language.zh')}</LanguageName>
          {i18n.language === 'zh' && <CheckIcon>✓</CheckIcon>}
        </LanguageOption>
      </DropdownMenu>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher; 